import React, { useState, useEffect } from "react";
import { ImBrightnessContrast } from "react-icons/im";
import {
    IconButton,
    Popover,
    Button,
    Divider,
    TextField,
    Box,
    Typography
} from "@mui/material";
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import PropTypes from "prop-types";
import {
    Sun,
} from "lucide-react";

const BrightnessComponent = ({ brightness, setBrightness, contrast, setContrast }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const [tempBrightness, setTempBrightness] = useState(brightness);
    const [tempContrast, setTempContrast] = useState(contrast);

    useEffect(() => {
        setTempBrightness(brightness);
    }, [brightness]);

    useEffect(() => {
        setTempContrast(contrast);
    }, [contrast]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const clearSettings = () => {
        setBrightness(0);
        setContrast(0);
    };

    return (
        <div>
            <IconButton
                onClick={handleClick}
            >
                <Sun size={16} />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                disablePortal
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: '#fff',
                        color: 'black',
                        boxShadow: 3,
                        borderRadius: 2,
                    },
                }}
            >
                <Box sx={{ p: 2, minWidth: 250, backgroundColor:'#fff' }}>
                    <Typography variant="body2">Brightness</Typography>
                    <Box display="flex" alignItems="center" mb={2} sx={{gap:"20px"}}>
                        <Box width={170}>
                            <Slider
                                value={tempBrightness}
                                onChange={val => setTempBrightness(val)}
                                onAfterChange={val => setBrightness(val)}
                                min={-1}
                                max={1}
                                step={0.05}
                                trackStyle={{ backgroundColor: '#1976d2' }}
                                handleStyle={{
                                    backgroundColor: '#ffffff',
                                    borderColor: '#1976d2',
                                }}
                                railStyle={{ backgroundColor: '#b0b0b0' }}
                            />
                        </Box>
                        <TextField
                            value={tempBrightness.toFixed(2)}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, width: 70 }}
                            inputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Typography variant="body2">Contrast</Typography>
                    <Box display="flex" alignItems="center" mb={2} sx={{gap:"20px"}}>
                        <Box width={170}>
                            <Slider
                                value={tempContrast}
                                onChange={val => setTempContrast(val)}
                                onAfterChange={val => setContrast(val)}
                                min={-100}
                                max={100}
                                step={1}
                                trackStyle={{ backgroundColor: '#1976d2' }}
                                handleStyle={{
                                    backgroundColor: '#ffffff',
                                    borderColor: '#1976d2',
                                }}
                                railStyle={{ backgroundColor: '#b0b0b0' }}
                            />
                        </Box>
                        <TextField
                            value={tempContrast}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, width: 70 }}
                            inputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Divider sx={{ mb: 1 }} />
                    <Box textAlign="right">
                        <Button
                            onClick={clearSettings}
                            variant="outlined"
                            size="small"
                            style={{
                                border: "1px solid rgba(60, 79, 118, 0.5)",
                                color: "#3C4F76",
                            }}
                        >
                            Clear
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </div>
    );
};

BrightnessComponent.propTypes = {
    brightness: PropTypes.number.isRequired,
    contrast: PropTypes.number.isRequired,
    setBrightness: PropTypes.func.isRequired,
    setContrast: PropTypes.func.isRequired,
};

export default React.memo(BrightnessComponent);
